'use client';

import { consoleLog } from '@/utils/console-log';
import { formatDate } from '@/utils/format-date';
import { getApplicationInsights } from '@/lib/application-insights';
import { IPageViewTelemetry } from '@microsoft/applicationinsights-web';
import { isLocalDevelopment } from '@/utils/is-local-host';
import { useAuth } from '@/components/Auth/AuthProvider/AuthProvider';
import { useEffect, useMemo } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

/**
 * AppInsights
 *
 * Client-only telemetry bridge for the application shell.
 *
 * Responsibilities:
 * - Lazily access the shared Application Insights singleton.
 * - Capture uncaught runtime exceptions from the browser global scope.
 * - Capture unhandled promise rejections that bubble to the global scope.
 * - Track page views when Next.js path/query changes.
 *
 * Design notes:
 * - This component renders no UI (`null`) and exists only for side effects.
 * - Route tracking is intentionally manual so route names are explicit and consistent
 *   with App Router state (`pathname` + `searchParams`).
 * - Error listeners are installed once per mounted singleton and cleaned up on unmount
 *   to avoid duplicate handlers during hot reloads or remounts.
 */
export function AppInsights() {
  // Current route path from Next.js App Router (e.g., '/manager').
  const pathname = usePathname();

  // Current query parameters object from Next.js App Router.
  // Converted to string only when needed for page-view naming.
  const searchParams = useSearchParams();

  // Acquire telemetry instance once for this component lifecycle.
  // `getApplicationInsights()` already handles all guards (flag/env/browser) and
  // returns null when telemetry should be disabled.
  const appInsights = useMemo(() => getApplicationInsights(), []);

  // Authentication state from the app's auth context, which may be relevant for
  // telemetry enrichment and conditional logging.
  const { isAuthenticated } = useAuth();

  // ================================
  // Global Error Tracking
  // ================================
  // Installs browser-level listeners for:
  // - synchronous uncaught errors (`error`)
  // - unhandled rejected promises (`unhandledrejection`)
  //
  // The listeners forward normalized Error objects to Application Insights.
  useEffect(() => {
    // No-op when telemetry is disabled or unavailable.
    if (!appInsights) return;
    if (isLocalDevelopment()) return;

    /**
     * Handle uncaught browser errors.
     *
     * `event.error` is preferred because it preserves stack and original error type.
     * Fallback to `event.message` when browsers emit message-only payloads.
     */
    const handleError = (event: ErrorEvent) => {
      appInsights.trackException({ exception: event.error ?? new Error(event.message) });
    };

    /**
     * Handle unhandled promise rejections.
     *
     * Rejection reasons can be anything (Error, string, object, etc.), so normalize
     * to an Error instance before sending to telemetry for consistent processing.
     */
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      appInsights.trackException({ exception: reason });
    };

    // Register global listeners after mount.
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup listeners on unmount/remount to prevent duplicate telemetry events.
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [appInsights]);

  // ================================
  // Route/Page View Tracking
  // ================================
  // Sends a page-view event whenever pathname or query params change.
  // This covers initial load and client-side navigation transitions.
  useEffect(() => {
    // No-op when telemetry is disabled or unavailable.
    if (!appInsights) return;
    if (isLocalDevelopment()) return;

    // Build stable route label for telemetry dashboards.
    // Example: '/manager?tab=team' or '/sales-rep'.
    const query = searchParams.toString();
    const route = query ? `${pathname}?${query}` : pathname;

    // Determine broad platform category for telemetry enrichment (mobile/tablet/desktop).
    const isMobile = /Mobi|iPhone|Android/i.test(navigator.userAgent);
    const isTablet = !isMobile && /iPad|Android/.test(navigator.userAgent);
    const platform = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';

    // Construct telemetry payload with route-centric name and full URI context.
    const data: IPageViewTelemetry = {
      name: window.document.title || route, // Fallback to route if document title is empty string.
      uri: window.location.href,
      refUri: document.referrer,
      startTime: new Date(),
      properties: {
        // Additional custom properties can be added here if needed.
        duration: performance.now(),
        environment: isLocalDevelopment() ? 'local' : process.env.NODE_ENV,
        isLoggedIn: isLocalDevelopment() ? true : isAuthenticated,
        pageType: 'web',
        queryParams: query, // Retain raw query string for filtering/grouping in App Insights.
        routePath: pathname, // Separate path for route-based analysis without query noise.
        startTimeFormatted: formatDate(new Date(), 'fullDatetime'), // Human-readable timestamp
        userAgent: navigator.userAgent, // Capture browser context for troubleshooting
        userLanguage: navigator.language, // Capture locale context for troubleshooting
        userPlatform: platform, // Capture broad platform category for analysis (mobile/tablet/desktop)
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    };

    // Send explicit page-view telemetry.
    // `name` is route-centric for grouping; `uri` retains full browser URL context.
    appInsights.trackPageView(data);

    // Log page view for debugging and verification.
    consoleLog.info('AppInsights: tracked page view', data);
  }, [appInsights, isAuthenticated, pathname, searchParams]);

  // Side-effect-only component.
  return null;
}
